/**
 * A score that explains itself.
 *
 * "75" tells a subscriber nothing. What they need is the arithmetic: which
 * signals fired on *this* row and what each contributed. The weights come from
 * /api/scoring — the same constants the pipeline scored with — so the sum
 * shown here always reconciles with the number it is attached to.
 *
 * Opens on hover and on keyboard focus, via CSS `:hover, :focus-within`. No
 * JavaScript state, so it cannot get stuck open, and a keyboard user gets the
 * same explanation a mouse user does.
 */

import type { Building, ScoringRules, Unit } from "../api/types";
import { humanise, pct } from "../lib/format";
import { milestoneCopy } from "../lib/milestone";
import { hasSirsAssessment } from "../lib/sirsAssessment";
import { hasUnpaidTaxes, unpaidTaxesDetail } from "../lib/taxDelinquency";

interface Line {
  label: string;
  detail?: string;
  points: number | null;
}

/**
 * What a line says when the row carries a signal this page's rules do not
 * know. The rules are fetched once per page load and the rows on every
 * request, so a tab left open across a release meets a newer signal than it
 * was taught — and a bare name with no points, under a total it does not add
 * up to, reads as an error in the arithmetic. The server syncs the catalog
 * before it copies a county, so a reload is the cure.
 */
export const STALE_RULES = "Reload for the current weights";

/** The engine's key for the SIRS assessment signal, at both levels. */
const SIRS = "sirs_assessment";
/** The engine's key for unpaid property taxes, a unit's signal only. */
const TAX = "tax_delinquent";

/**
 * Which way the popover opens. The drawer scrolls, so a score near its foot
 * must open upward or the panel is clipped by the drawer's own overflow.
 */
export type Placement = "down" | "up";

export function UnitScore({
  unit,
  rules,
  placement = "down",
}: {
  unit: Unit;
  rules: ScoringRules | null;
  placement?: Placement;
}) {
  const lines: Line[] = [];

  if (rules) {
    const byFlag = new Map(rules.signals.map((s) => [s.flag, s]));
    for (const flag of unit.flags) {
      const signal = byFlag.get(flag);
      // The years and the amount say what the +25 is for; the served
      // description stays the fallback where the list did not reach the row.
      const detail = flag === TAX && hasUnpaidTaxes(unit)
        ? unpaidTaxesDetail(unit) : signal?.description ?? "";
      lines.push(signal
        ? { label: signal.label, detail, points: signal.points }
        : { label: humanise(flag), detail: STALE_RULES, points: null });
    }
  }

  return (
    <Popover score={unit.score} lines={lines} rules={rules} total={unit.score}
             placement={placement} />
  );
}

export function BuildingScore({
  building,
  rules,
  placement = "down",
}: {
  building: Building;
  rules: ScoringRules | null;
  placement?: Placement;
}) {
  const lines: Line[] = [];
  let subtotal = 0;

  if (rules) {
    const w = rules.building_weights;
    const cases: Array<[number, number, string]> = [
      [building.unsafe_structure_cases, w.unsafe_structure ?? 0, "unsafe-structure case"],
      [building.lien_cases, w.lien ?? 0, "lien case"],
      [building.expired_permit_cases, w.expired_permit ?? 0, "expired-permit case"],
    ];
    for (const [count, weight, noun] of cases) {
      if (!count) continue;
      lines.push({
        label: `${count} ${noun}${count === 1 ? "" : "s"}`,
        detail: `${count} × ${weight}`,
        points: count * weight,
      });
      subtotal += count * weight;
    }

    const milestone = rules.milestone.find(
      (m) => m.status === building.milestone_status,
    );
    if (milestone && milestone.points > 0) {
      lines.push({
        label: milestone.label,
        detail: milestone.authoritative ? "County record" : "Our estimate",
        points: milestone.points,
      });
      subtotal += milestone.points;
    }
  }

  // The concentration multiplier is the reason a building view exists: nine
  // bad units in 24 is a building problem, nine in 400 is not.
  const rate = building.distress_rate;
  if (rules && rate && subtotal) {
    lines.push({
      label: `× ${(1 + rate).toFixed(2)} concentration`,
      detail: `${pct(rate)} of units in distress`,
      points: null,
    });
  }

  // After the multiplier, because the engine adds it after: the association's
  // report is about the whole building, not about how many units carry a case.
  if (rules && hasSirsAssessment(building)) {
    const signal = rules.building_signals.find((s) => s.key === SIRS);
    const weight = rules.building_weights[SIRS];
    lines.push({
      label: signal?.label ?? "SIRS special assessment reported",
      detail: weight === undefined ? STALE_RULES : "Filed with DBPR",
      points: weight ?? null,
    });
  }

  return (
    <Popover
      score={building.building_score}
      lines={lines}
      rules={rules}
      total={building.building_score}
      placement={placement}
      footnote={
        building.milestone_source === "estimated" && building.milestone_overdue
          ? milestoneCopy(building).basis
          : undefined
      }
    />
  );
}

function Popover({
  score,
  lines,
  rules,
  total,
  footnote,
  placement = "down",
}: {
  score: number;
  lines: Line[];
  rules: ScoringRules | null;
  total: number;
  footnote?: string;
  placement?: Placement;
}) {
  const label = lines.length
    ? `Score ${score}. ${lines.length} contributing signal${lines.length === 1 ? "" : "s"}.`
    : `Score ${score}.`;

  return (
    <span className="score-wrap">
      <span className="score" tabIndex={0} role="button" aria-label={label}>
        {score}
      </span>

      <span className={`score-pop ${placement}`} role="tooltip">
        <span className="score-pop-head">Score {score}</span>

        {lines.length === 0 ? (
          <span className="score-pop-empty">
            {rules ? "No scoring signals on this record." : "Loading…"}
          </span>
        ) : (
          <span className="score-pop-lines">
            {lines.map((line, i) => (
              <span className="score-pop-line" key={i}>
                <span className="score-pop-label">
                  {line.label}
                  {line.detail && (
                    <span className="score-pop-detail">{line.detail}</span>
                  )}
                </span>
                <span className="score-pop-points">
                  {line.points === null ? "" : `+${line.points}`}
                </span>
              </span>
            ))}
            <span className="score-pop-line total">
              <span className="score-pop-label">Total</span>
              <span className="score-pop-points">{total}</span>
            </span>
          </span>
        )}

        {footnote && <span className="score-pop-foot">{footnote}</span>}
        {rules && <span className="score-pop-foot">{rules.caveat}</span>}
      </span>
    </span>
  );
}
